import React, { useState, useRef, useEffect } from 'react';
import { ref, push, set, onValue } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { database, storage } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import { ICONS } from "../constants/icons";
import { convertToWebP } from "../utils/utils";
import LoadingModal from "./LoadingModal";

const SubmitTestimonialModal = ({ isOpen, onClose, productId, courseTitle }) => {
    const { currentUser } = useUser();
    const [name, setName] = useState(currentUser?.name || "");
    const [text, setText] = useState("");
    const [rating, setRating] = useState(5);
    const [image, setImage] = useState(null);
    const [existingImageUrl, setExistingImageUrl] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !productId || !currentUser?.uid) return;

        // Fetch existing testimonial if it exists
        const testimonialRef = ref(database, `Listings/${productId}/testimonials/${currentUser.uid}`);
        onValue(testimonialRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                setName(data.name || currentUser.name || "");
                setText(data.text || "");
                setRating(data.rating || 5);
                setExistingImageUrl(data.imageUrl || "");
            }
        }, { onlyOnce: true });
    }, [isOpen, productId, currentUser]);

    if (!isOpen) return null;

    const handleImageChange = (e) => {
        if (e.target.files[0]) {
            setImage(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!text.trim()) {
            window.showToast("Please enter your feedback", "error");
            return;
        }

        setIsSubmitting(true);
        setLoadingMessage("Submitting your testimonial...");

        try {
            let imageUrl = existingImageUrl;
            if (image) {
                setLoadingMessage("Optimizing image...");
                const webpFile = await convertToWebP(image);
                
                setLoadingMessage("Uploading screenshot...");
                const imagePath = `testimonials/${productId}/${currentUser.uid}_${Date.now()}.webp`;
                const storageReference = storageRef(storage, imagePath);
                await uploadBytes(storageReference, webpFile);
                imageUrl = await getDownloadURL(storageReference);
            }

            const testimonialRef = ref(database, `Listings/${productId}/testimonials/${currentUser.uid}`);
            
            await set(testimonialRef, {
                id: currentUser.uid,
                userId: currentUser.uid,
                name: name || "Anonymous",
                text: text,
                rating: rating,
                imageUrl: imageUrl,
                featured: false, // Reset to private on edit for re-approval
                isUserGenerated: true,
                createdAt: Date.now()
            });

            window.showToast("Testimonial updated! It will be visible once re-approved by the creator.", "success");
            onClose();
            // Reset form
            setText("");
            setRating(5);
            setImage(null);
        } catch (error) {
            console.error("Error submitting testimonial:", error);
            window.showToast("Failed to submit testimonial", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <LoadingModal showLoadingModal={isSubmitting} loadingMessage={loadingMessage} />
            
            <div className="bg-white dark:bg-[#1F1F1F] w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-800 animate-in fade-in zoom-in duration-300">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold dark:text-white">Share your experience</h2>
                        <button 
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>

                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        How was your experience with <span className="font-semibold text-[#327691]">{courseTitle}</span>? Your feedback helps the creator and other students.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Name Field */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Your Name</label>
                            <input 
                                type="text" 
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Enter your name"
                                className="w-full rounded-xl px-4 py-3 bg-[#F5F9FE] dark:bg-white text-black outline-none border border-gray-100 dark:border-0"
                            />
                        </div>

                        {/* Rating Selector */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Rating</label>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        type="button"
                                        onClick={() => setRating(star)}
                                        className="focus:outline-none transition-transform hover:scale-110"
                                    >
                                        <svg 
                                            width="28" 
                                            height="28" 
                                            viewBox="0 0 24 24" 
                                            fill={star <= rating ? "#FFD700" : "none"} 
                                            stroke={star <= rating ? "#FFD700" : "#D1D5DB"}
                                            strokeWidth="2"
                                            strokeLinecap="round" 
                                            strokeLinejoin="round"
                                        >
                                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                                        </svg>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Feedback Text */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Feedback</label>
                            <textarea 
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="What did you like most about the course?"
                                rows="4"
                                className="w-full rounded-xl px-4 py-3 bg-[#F5F9FE] dark:bg-white text-black outline-none border border-gray-100 dark:border-0 resize-none"
                            ></textarea>
                        </div>

                        {/* Screenshot Upload */}
                        <div 
                            className="w-full cursor-pointer border-dashed border-2 border-gray-200 dark:border-gray-700 p-6 text-center rounded-2xl bg-[#F5F9FE]/50 dark:bg-white/5 hover:bg-[#F5F9FE] dark:hover:bg-white/10 transition-colors"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input 
                                ref={fileInputRef}
                                type="file" 
                                accept="image/*"
                                className="hidden"
                                onChange={handleImageChange}
                            />
                            <img src={ICONS.upload} className="mx-auto mb-2 w-8 h-8 opacity-60" alt="Upload" />
                            <p className="font-semibold text-sm text-gray-600 dark:text-gray-300">Add a screenshot (Optional)</p>
                            <p className="text-xs text-gray-400 mt-1">Show off your progress or results!</p>
                            
                            {image && (
                                <div className="mt-3 flex items-center justify-center gap-2 text-xs text-[#327691] font-medium">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    {image.name}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button 
                                type="button"
                                onClick={onClose}
                                className="flex-1 py-3 rounded-xl font-semibold text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 py-3 rounded-xl bg-[#327691] text-white font-bold shadow-lg shadow-[#327691]/20 hover:opacity-90 transition-opacity"
                            >
                                {isSubmitting ? "Submitting..." : "Submit Feedback"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SubmitTestimonialModal;
